/**
 * Service Worker registration and lifecycle manager.
 */

/**
 * Unregisters any active Service Workers and purges legacy caches.
 *
 * Why: Resolves stale cache locks or corrupted worker interceptors during development,
 * ensuring map tiles and live network assets flow directly without proxy failures.
 *
 * @returns Promise resolving when unregistration and cache deletion complete
 */
export async function unregisterServiceWorkers(): Promise<void> {
  if (
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    typeof navigator.serviceWorker?.getRegistrations === 'function'
  ) {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      for (const reg of registrations) {
        if (typeof reg.unregister === 'function') {
          await reg.unregister();
        }
      }
      if (typeof caches !== 'undefined' && typeof caches.keys === 'function') {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map((name) => caches.delete(name)));
      }
      console.info('[NodeRadar] All active service workers and caches cleared.');
    } catch (err) {
      console.warn('[NodeRadar] Failed to unregister service workers:', err);
    }
  }
}

/**
 * Registers the Service Worker for offline application asset caching.
 *
 * Why: Mobile field monitoring takes place in remote environments and dead zones. Pre-caching application
 * assets ensures NodeRadar remains functional offline while bypassing cross-origin raster tiles.
 *
 * @param swPath - Relative or absolute path to the service worker script
 * @param force - If true, bypasses the development mode check
 * @returns Promise resolving to ServiceWorkerRegistration or null if unsupported or in development
 */
export async function registerServiceWorker(swPath = './sw.js', force = false): Promise<ServiceWorkerRegistration | null> {
  // During local development, unregister service workers to prevent stale tile interceptors and HMR conflicts unless forced
  if (!force && typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.DEV) {
    await unregisterServiceWorkers();
    return null;
  }

  if (
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    typeof window !== 'undefined' &&
    (window.location?.protocol === 'https:' ||
      window.location?.hostname === 'localhost' ||
      window.location?.hostname === '127.0.0.1')
  ) {
    try {
      const registration = await navigator.serviceWorker.register(swPath, {
        scope: './'
      });
      console.info('[NodeRadar] ServiceWorker registered with scope:', registration.scope);
      return registration;
    } catch (err) {
      console.warn('[NodeRadar] ServiceWorker registration failed:', err);
      return null;
    }
  }
  return null;
}
